import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  Link,
  CircularProgress,
  useTheme,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import ArticleIcon from '@mui/icons-material/Article';
import { useTranslation } from 'react-i18next';
import { hostApiClient } from '../hostApiClient';

interface RegulationEditorProps {
  /** Regulation as full text or URL. If present, user must accept before booking. */
  value: string | undefined;
  onChange: (regulation: string | undefined) => void;
  disabled?: boolean;
  /** Optional override for the section title (defaults to space.regulation). */
  title?: string;
  /** Optional override for help copy (defaults to space.regulationHelp). */
  helpText?: string;
  /** Hide the title/help block when the parent already renders a section heading. */
  hideTitle?: boolean;
}

const isUrl = (s: string): boolean => /^https?:\/\//i.test(s.trim());
const isS3Url = (s: string): boolean => /\.s3\.|s3\.amazonaws\.com/i.test(s);

const RegulationEditor: React.FC<RegulationEditorProps> = ({
  value,
  onChange,
  disabled = false,
  title,
  helpText,
  hideTitle = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [fileUploading, setFileUploading] = useState(false);
  const [textDialogOpen, setTextDialogOpen] = useState(false);
  const [textDraft, setTextDraft] = useState('');

  const hasValue = Boolean(value?.trim());
  const isUploadedFile = hasValue && isUrl(value!) && isS3Url(value!);
  const hasText = hasValue && !isUrl(value!);

  const handleOpenTextDialog = () => {
    setTextDraft(hasText ? value! : '');
    setTextDialogOpen(true);
  };

  const handleSaveTextDialog = () => {
    onChange(textDraft.trim() || undefined);
    setTextDialogOpen(false);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setFileUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'pdf';
      const res = await hostApiClient.generateUploadUrls({
        fileExtensions: [ext],
      });
      const url = res.uploadUrls[0];
      await hostApiClient.uploadToPresignedUrl(url.uploadTo, file);
      onChange(url.accessAt);
    } catch (err) {
      console.error('Regulation file upload failed:', err);
    } finally {
      setFileUploading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {!hideTitle && (
        <>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            {title ?? t('space.regulation', 'Terms and Conditions (T&C)')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {helpText ??
              t(
                'space.regulationHelp',
                'Organization-wide T&C via text or file. Applies to all spaces; users must accept before booking.'
              )}
          </Typography>
        </>
      )}

      {/* Text T&C: button + popup */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
        sx={{ width: '100%', mb: hideTitle ? 0 : 2 }}
      >
        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
          {hasText ? (
            <Button
              variant="outlined"
              startIcon={<ArticleIcon />}
              onClick={handleOpenTextDialog}
              disabled={disabled}
            >
              {t('space.regulationEditText', 'Edit T&C text')} ({value!.length} {t('space.characters', 'chars')})
            </Button>
          ) : (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleOpenTextDialog}
              disabled={disabled}
            >
              {isUploadedFile
                ? t('space.regulationReplaceWithText', 'Replace with text')
                : t('space.regulationAddText', 'Add T&C text')}
            </Button>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
            {t('common.or', 'or')}
          </Typography>

          <Button
            variant="outlined"
            component="label"
            startIcon={fileUploading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
            disabled={disabled || fileUploading}
            sx={{
              border: '1px dashed',
              borderColor: theme.palette.primary.main,
              py: 1,
              px: 2,
            }}
          >
            {fileUploading ? t('common.uploading', 'Uploading...') : t('space.regulationUploadFile', 'Upload PDF or document')}
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx"
              onChange={handleFileSelect}
            />
          </Button>

          {hasValue && isUrl(value!) && (
            <Link href={value!} target="_blank" rel="noopener noreferrer" variant="body2">
              {t('space.regulationViewFile', 'View T&C file')}
            </Link>
          )}
        </Stack>

        {hasValue ? (
          <Button
            size="small"
            color="secondary"
            onClick={() => onChange(undefined)}
            disabled={disabled}
          >
            {t('common.remove', 'Remove')}
          </Button>
        ) : null}
      </Stack>

      <Dialog open={textDialogOpen} onClose={() => setTextDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: 400 } }}>
        <DialogTitle>{t('space.regulationTextContent', 'T&C content')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={14}
            value={textDraft}
            onChange={(e) => setTextDraft(e.target.value)}
            placeholder={t('space.regulationTextPlaceholder', 'Paste or type your terms and conditions here...')}
            disabled={disabled}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTextDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleSaveTextDialog}>
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegulationEditor;
